import { useState } from "react"
import { Link as RouterLink } from "react-router-dom"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Fade,
  Link,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import StopIcon from "@mui/icons-material/StopCircleOutlined"
import PauseIcon from "@mui/icons-material/PauseCircleOutlined"
import ResumeIcon from "@mui/icons-material/PlayCircleOutlined"
import TimerIcon from "@mui/icons-material/TimerOutlined"
import WarningIcon from "@mui/icons-material/ReportProblemOutlined"

import { useTimer } from "../context/TimerContext"
import { clockFormat, humanDuration } from "../time/duration"

/**
 * The running timer, everywhere in the app.
 *
 * ── Why it lives in the shell rather than on the tasks page ─────────────────
 *
 * Somebody starts a timer and then goes to read a chat thread, open a project,
 * answer an email. A timer only visible where it was started is a timer left
 * running overnight, and the fix for that is one clock that follows them.
 *
 * ── Why the seconds are shown ───────────────────────────────────────────────
 *
 * Not for precision — nobody bills by the second. A number that visibly moves is
 * the difference between a bar somebody trusts and a bar they check against
 * their own watch. It is also the cheapest possible proof that the timer is
 * still running.
 */
export default function TimerBar() {
  const { running, elapsed, busy, paused, longRunning, stop, pause, resume, justStopped, dismissStopped, error, clearError } =
    useTimer()
  const [confirmingLong, setConfirmingLong] = useState(false)

  const onStop = async () => {
    try {
      await stop()
      setConfirmingLong(false)
    } catch {
      // The context holds the message; the snackbar below shows it.
    }
  }

  return (
    <>
      {/*
        Collapse rather than a conditional: a bar that appears instantly shoves
        the page down under the reader's eyes. Growing into place over 200ms
        reads as the bar arriving rather than the page jumping.
      */}
      <Collapse in={Boolean(running)} timeout={220} unmountOnExit>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            borderBottom: 1,
            borderColor: "divider",
            transition: (theme) => theme.transitions.create("background-color"),
            bgcolor: (theme) => {
              // Paused reads as a quieter version of running, not as a different
              // component: same bar, same place, drained of the brand colour.
              if (paused) return theme.palette.action.hover
              return theme.palette.mode === "dark" ? "rgba(126,211,72,0.10)" : "rgba(126,211,72,0.16)"
            },
          }}
        >
          {/* The dot is the liveness signal for anyone who is not watching the
              digits change — and it stops if the tab is throttled, which is
              honest: the number has stopped moving too. */}
          <Box
            aria-hidden
            sx={{
              width: 9,
              height: 9,
              borderRadius: "50%",
              bgcolor: paused ? "text.disabled" : "primary.main",
              flexShrink: 0,
              // Still while paused. The dot means "this is counting"; leaving it
              // beating over a frozen clock would be the one animation in the app
              // that says something untrue.
              animation: paused ? "none" : "tw-timer-pulse 1.6s ease-in-out infinite",
              "@keyframes tw-timer-pulse": {
                "0%, 100%": { opacity: 1, transform: "scale(1)" },
                "50%": { opacity: 0.35, transform: "scale(0.8)" },
              },
              // Somebody who has asked for less motion still gets the colour.
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          />

          <Typography
            variant="h3"
            component="p"
            aria-live="off"
            sx={{
              fontVariantNumeric: "tabular-nums",
              // Without tabular figures every digit change nudges the text after
              // it, and a clock that jitters looks broken.
              minWidth: 92,
              lineHeight: 1,
              color: paused ? "text.secondary" : "text.primary",
              transition: (theme) => theme.transitions.create("color"),
            }}
          >
            {clockFormat(elapsed)}
          </Typography>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {running?.task_code ? (
                <Link component={RouterLink} to={`/tasks/${running.task_id}`} underline="hover" color="inherit">
                  {running.task_code}: {running.task_title}
                </Link>
              ) : (
                running?.description || "Untitled work"
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {paused ? "Paused · " : ""}
              {[running?.client_label, running?.project_name].filter(Boolean).join(" · ") || "No project"}
              {running?.is_billable ? "" : " · not billable"}
            </Typography>
          </Box>

          {longRunning && !paused ? (
            <Tooltip title="This has been running a long time. Was it left on?">
              <Chip
                size="small"
                icon={<WarningIcon sx={{ fontSize: 16 }} />}
                label={humanDuration(elapsed)}
                color="warning"
                variant="outlined"
              />
            </Tooltip>
          ) : null}

          {/*
            Pause first, stop second, and stop stays the filled one.
            Pausing is reversible and stopping is not — the destructive-ish
            action keeps the weight, and the reversible one sits beside it as an
            outline so the pair cannot be hit by muscle memory alone.
          */}
          <Tooltip title={paused ? "Resume — the clock picks up where it left off" : "Pause — the gap is not counted"}>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={paused ? <ResumeIcon /> : <PauseIcon />}
              disabled={busy}
              onClick={() => (paused ? resume() : pause()).catch(() => {})}
              aria-label={paused ? "Resume the timer" : "Pause the timer"}
              sx={{ borderColor: "divider" }}
            >
              {paused ? "Resume" : "Pause"}
            </Button>
          </Tooltip>

          <Button
            size="small"
            variant="contained"
            color={longRunning ? "warning" : "primary"}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <StopIcon />}
            disabled={busy}
            onClick={() => (longRunning ? setConfirmingLong(true) : onStop())}
            aria-label="Stop the running timer"
          >
            Stop
          </Button>
        </Box>
      </Collapse>

      {/*
        A long-running timer gets a question rather than a silent stop.
        Truncating it would be inventing data, and stopping it without asking
        loses the case where somebody really did work eight hours straight.
      */}
      <Collapse in={confirmingLong} timeout={200} unmountOnExit>
        <Alert
          severity="warning"
          icon={<TimerIcon />}
          sx={{ borderRadius: 0 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button size="small" color="inherit" onClick={() => setConfirmingLong(false)}>
                Keep running
              </Button>
              <Button size="small" variant="contained" color="warning" onClick={onStop} disabled={busy}>
                Stop at {humanDuration(elapsed)}
              </Button>
            </Stack>
          }
        >
          This timer has been running for {humanDuration(elapsed)}. Stop it at that, or keep going and edit the entry
          afterwards.
        </Alert>
      </Collapse>

      <Snackbar
        open={Boolean(justStopped)}
        autoHideDuration={6000}
        onClose={dismissStopped}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionComponent={Fade}
      >
        <Alert severity="success" variant="filled" onClose={dismissStopped} sx={{ alignItems: "center" }}>
          Logged {humanDuration(justStopped?.duration_seconds)}
          {justStopped?.description ? ` — ${justStopped.description}` : ""}
        </Alert>
      </Snackbar>

      <Snackbar open={Boolean(error)} autoHideDuration={8000} onClose={clearError} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="error" variant="filled" onClose={clearError}>
          {error}
        </Alert>
      </Snackbar>
    </>
  )
}
